import { Media } from '@/lib/payload/types';
import Image, { ImageProps } from 'next/image';

export type ImageSize = keyof NonNullable<Media['sizes']>;

const sizeOrder: ImageSize[] = ['small', 'thumbnail', 'card', 'tablet'];

const ErrorImage = ({
  message,
  imageProps,
}: {
  message?: string;
  imageProps?: PayloadImageProps;
}) => (
  <div
    style={{
      width: imageProps?.width ?? '100%',
      height: imageProps?.height ?? '100%',
      background: 'var(--color-background-darker)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1em',
    }}
  >
    {message || 'An error occurred trying to load this Payload image.'}
  </div>
);

export type PayloadImageProps = {
  image?: Media | number | string | null;
  size?: ImageSize;
  width?: number;
  height?: number;
  quality?: number;
  defaultWidth?: number;
  defaultHeight?: number;
} & Omit<ImageProps, 'src' | 'alt' | 'width' | 'height' | 'quality'>;

const PayloadImage: React.FC<PayloadImageProps> = ({
  image,
  size,
  width: userWidth,
  height: userHeight,
  quality,
  defaultWidth = 100,
  defaultHeight = 100,
  ...props
}) => {
  if (!image) return <ErrorImage message="No image data provided" />;
  if (typeof image === 'string' || typeof image === 'number')
    return <ErrorImage message="Image relation not resolved in data" />;

  // Get the correct size
  let src: string | undefined | null = '';

  if (!size || !image.sizes) {
    // If no sizes provided, just use default URL
    src = image.url;
  } else {
    // There are multiple sizes, check from the preferred size upwards for the next best
    const startIdx = sizeOrder.indexOf(size);
    for (let i = startIdx; i < sizeOrder.length; i++) {
      if (sizeOrder[i] in image.sizes) {
        src = image.sizes[sizeOrder[i]]?.url;
        break;
      }
    }
  }
  if (!src) {
    src = image.url || '';
  }

  const alt = image.alt || `Image ${image.id}`;
  const width = userWidth ?? image.width ?? defaultWidth;
  const height = userHeight ?? image.height ?? defaultHeight;

  return <Image src={src} alt={alt} width={width} height={height} quality={quality} {...props} />;
};

export default PayloadImage;
